import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  primary: 'border border-transparent bg-brand text-oncolor shadow-sm hover:bg-brand/90 hover:shadow-card active:bg-brand disabled:bg-line-strong disabled:text-ink-muted',
  secondary:
    'bg-surface text-ink border border-line hover:border-line-strong hover:bg-surface-muted',
  ghost: 'bg-transparent text-ink-soft hover:bg-surface-muted hover:text-ink',
  danger: 'bg-danger text-white hover:bg-danger/90',
  success: 'bg-brand text-oncolor hover:bg-brand/90',
}

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-[13px] gap-1.5 rounded-field',
  xs: 'h-8 px-3 text-[12.5px] gap-1 rounded-[8px]',
  md: 'h-11 px-5 text-[15px] gap-2 rounded-field',
  lg: 'h-[52px] px-7 text-base gap-2.5 rounded-field',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  /** Right-aligned affordance, e.g. the ⏎ chip on the wizard's Continue. */
  trailing?: ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    trailing,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // `loading` implies disabled so a slow request cannot be double-submitted.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center font-semibold transition-[background-color,border-color,box-shadow,transform] duration-150 active:translate-y-px motion-reduce:transform-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {trailing && !loading && <span className="ml-1 opacity-80">{trailing}</span>}
    </button>
  )
})

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** The ⏎ chip shown inside the wizard's primary action. */
export function EnterChip() {
  return (
    <span className="ml-1 grid h-[22px] w-[22px] place-items-center rounded-[5px] bg-oncolor/15 font-mono text-[11px] leading-none">
      ⏎
    </span>
  )
}
